// bundled as one file for now due to some questions about compoent architecture.

import { ENV_VARS } from "@/shared/env";
// data layer
import { useUser } from "@/shared/providers/user-provider";
import useSWRMutation from "swr/mutation";
import { z } from "zod";

const ModelTermAcceptanceZod = z.object({
  id: z.string(),
  model_id: z.string(),
  org_id: z.string(),
  user_id: z.string(),
  terms_url: z.string(),
  created_at: z.number(),
});

export type ModelTermAcceptance = z.infer<typeof ModelTermAcceptanceZod>;

const modelTermAcceptanceURL = (orgID: string, modelID: string) => {
  return `${ENV_VARS.BASE_API_URL}/platform/v1/organizations/${orgID}/models/${modelID}/model_terms_acceptances`;
};

export function useCreateModelTermsAcceptance(
  modelID: string,
  termsURL: string,
) {
  const { buildRequestHeaders, currentOrg } = useUser();
  const url = modelTermAcceptanceURL(currentOrg || "", modelID);
  const createModelTermsAcceptance = async (url: string) => {
    const resp = await fetch(url, {
      method: "POST",
      headers: await buildRequestHeaders(),
      body: JSON.stringify({
        terms_url: termsURL,
      }),
    });
    if (!resp.ok) {
      const error = new Error(
        `Model terms acceptance creation step failed (Status: ${resp.status})`,
      );
      throw error;
    }
    return await resp.json();
  };

  return useSWRMutation(url, createModelTermsAcceptance);
}

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
// ui layer
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toastError } from "@/shared/logging/logger";
import { ExternalLink } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";

export const ModelTermAcceptanceDialog = ({
  id,
  termsURL,
  children,
  onSuccess,
  onClose,
}: {
  id: string;
  termsURL: string;
  children?: React.ReactNode;
  onSuccess?: () => void;
  onClose?: () => void;
}) => {
  const { currentOrgFull } = useUser();
  const [open, setOpen] = useState(false);
  const [modelTermAcceptance, setModelTermAcceptance] = useState(false);
  const [locationOrigin, setLocationOrigin] = useState("");
  const { trigger, error, data } = useCreateModelTermsAcceptance(id, termsURL);
  useEffect(() => {
    if (error) {
      toastError("An error occurred", error);
    }
  }, [error]);
  useEffect(() => {
    if (!error && data) {
      setOpen(false);
      onSuccess?.();
    }
  }, [error, data]);

  const handleSubmit = () => {
    if (!modelTermAcceptance) {
      toastError(
        "You must accept the terms to continue",
        new Error("Checkbox not checked"),
      );
      return;
    }
    trigger();
  };

  useEffect(() => {
    if (onClose) {
      handleOpenChange(true);
    }
  }, [onClose]);

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (!open) {
      onClose?.();
    }
  };

  useEffect(() => {
    setLocationOrigin(window.location.origin);
  }, []);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!onClose && (
        <DialogTrigger asChild>
          {children || <Button>Accept Terms</Button>}
        </DialogTrigger>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Model Terms Acceptance</DialogTitle>
        </DialogHeader>
        {currentOrgFull?.role === "owner" ? (
          <div>
            <p className="mb-0 mt-3 text-sm">
              The model <span className="font-bold">{id}</span> for the
              organization{" "}
              <span className="font-bold">{currentOrgFull?.id}</span> requires
              acceptance of the following terms:
            </p>
            <p className="mb-7 text-sm">
              <Link
                href={termsURL}
                target="_blank"
                className="text-primaryaccent"
              >
                {termsURL}
                <ExternalLink className="inline ml-1 mt-[-4px]" size={16} />
              </Link>
            </p>
            <div className="flex gap-2 items-center">
              <Checkbox
                id="model-terms-acceptance"
                checked={modelTermAcceptance}
                onCheckedChange={(c) => setModelTermAcceptance(c === true)}
              />
              <label
                htmlFor="model-terms-acceptance"
                className="pl-1 text-sm font-medium"
              >
                I have read the above terms and accept
              </label>
            </div>
            <div className="flex justify-end">
              <Button onClick={handleSubmit}>Accept Terms</Button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm">
              The model <span className="font-bold">{id}</span> requires
              acceptance from your organization{" "}
              <span className="font-bold">({currentOrgFull?.id})</span> admin.
            </p>
            <p className="mb-7 text-sm">
              <Link
                href={termsURL}
                target="_blank"
                className="text-primaryaccent"
              >
                {termsURL}
                <ExternalLink className="inline ml-1 mt-[-4px]" size={16} />
              </Link>
            </p>
            <p className="text-sm mb-2">
              Have your organization admin accept the terms here:
            </p>
            <Input
              value={`${locationOrigin}/playground?model=${id}&terms-check=true`}
              readOnly
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
